import { Eye } from 'lucide-react'
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

/**
 * Shared bits for the Reports tab (Store Inventory → Reports).
 */

/** Quantity formatter: whole numbers plain, fractions to 2 dp, no float tails. */
export const fmtQty = (n: number) => {
  const rounded = Math.round(n * 100) / 100
  return Number.isInteger(rounded) ? String(rounded) : String(rounded)
}

/** `12.5 kg` with the unit, for table cells. */
export const fmtQtyUnit = (n: number, unit: string) => `${fmtQty(n)} ${unit}`

/**
 * Contextual-help affordance: an accessible eye icon with a short plain-
 * language explanation. Every card and metric on the Reports tab carries one.
 */
export function MetricHint({ text, label }: { text: string; label?: string }) {
  return (
    <UITooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label ? `About ${label}` : 'More info'}
          className="inline-flex items-center justify-center rounded-full p-0.5 text-muted-foreground/60 hover:text-foreground hover:bg-muted/50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Eye className="w-3.5 h-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{text}</TooltipContent>
    </UITooltip>
  )
}

/** One small labelled figure inside a section card. */
export function StatTile({ label, value, hint, sub, tone = '' }: {
  label: string
  value: string
  hint?: string
  sub?: string
  tone?: string
}) {
  return (
    <div className="min-w-0">
      <p className={`text-xl font-bold truncate tabular-nums ${tone}`} title={value}>{value}</p>
      <div className="flex items-center gap-1.5 mt-0.5">
        <p className="text-xs text-muted-foreground truncate">{label}</p>
        {hint && <MetricHint text={hint} label={label} />}
      </div>
      {sub && <p className="text-[11px] text-muted-foreground/80 mt-0.5">{sub}</p>}
    </div>
  )
}

/** An item the count dialog can be opened for, from anywhere on the page. */
export type CountTarget = {
  id: string
  name: string
  unit: string
  quantity_on_hand: number
  unit_cost?: number
}
